// InspectionForm.jsx - Simplified vehicle inspection form with uploads
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { FileUpload, PhotoGallery, LoadingSpinner, ErrorDisplay } from './shared';
import VinDisplay from '../shared/VinDisplay';

const InspectionForm = ({ vehicleId, users, onBack, onROCreated, showCosts }) => {
  // State - simplified to just uploads + general findings
  const [vehicle, setVehicle] = useState(null);
  const [inspection, setInspection] = useState({
    inspectionType: 'full',
    walkaroundVideoUrl: '',
    bluedriverScanUrl: '',
    photos: [],
    generalFindings: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showCreateROModal, setShowCreateROModal] = useState(false);
  const [error, setError] = useState(null);

  // Load vehicle and existing inspection data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Get vehicle
      const vehicles = await api.getVehicles();
      const foundVehicle = vehicles.find(v => v.id === vehicleId);
      if (!foundVehicle) {
        setError('Vehicle not found');
        setLoading(false);
        return;
      }
      setVehicle(foundVehicle);

      // Get existing inspection if any
      const existingInspection = await api.getVehicleInspection(vehicleId);
      if (existingInspection) {
        setInspection(prev => ({
          ...prev,
          ...existingInspection,
          photos: existingInspection.photos || []
        }));
      } else {
        // Set inspection type based on vehicle's inspection type
        setInspection(prev => ({
          ...prev,
          inspectionType: foundVehicle.inspectionType === 'wholesale_platform' ? 'basic' : 'full'
        }));
      }
    } catch (err) {
      console.error('Failed to load inspection data:', err);
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-save inspection data with saved indicator
  const saveInspection = async (data = inspection) => {
    if (!vehicleId) return;
    setSaving(true);
    setSaved(false);
    try {
      await api.saveVehicleInspection(vehicleId, data);
      setSaved(true);
      // Hide saved indicator after 2 seconds
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save inspection:', err);
    } finally {
      setSaving(false);
    }
  };

  // Handle field changes with auto-save on blur
  const handleFieldChange = (field, value) => {
    setInspection(prev => {
      const updated = { ...prev, [field]: value };
      return updated;
    });
  };

  const handleFieldBlur = () => {
    saveInspection();
  };

  // Handle photo upload
  const handlePhotoUpload = (url) => {
    setInspection(prev => {
      const updated = { ...prev, photos: [...(prev.photos || []), url] };
      saveInspection(updated);
      return updated;
    });
  };

  // Handle photo delete
  const handlePhotoDelete = (index) => {
    setInspection(prev => {
      const updated = { ...prev, photos: prev.photos.filter((_, i) => i !== index) };
      saveInspection(updated);
      return updated;
    });
  };

  // Handle video upload
  const handleVideoUpload = (url) => {
    setInspection(prev => {
      const updated = { ...prev, walkaroundVideoUrl: url };
      saveInspection(updated);
      return updated;
    });
  };

  // Handle scan upload
  const handleScanUpload = (url) => {
    setInspection(prev => {
      const updated = { ...prev, bluedriverScanUrl: url };
      saveInspection(updated);
      return updated;
    });
  };

  // Handle underbody video upload
  const handleUnderbodyVideoUpload = (url) => {
    setInspection(prev => {
      const updated = { ...prev, underbodyVideoUrl: url };
      saveInspection(updated);
      return updated;
    });
  };

  // Handle "No Work Needed" - mark vehicle as ready without creating RO
  const handleNoWorkNeeded = async () => {
    if (!window.confirm('Mark this vehicle as ready with no work needed? This will complete the inspection without creating a Repair Order.')) {
      return;
    }
    
    setSaving(true);
    try {
      // Save current inspection data first
      await api.saveVehicleInspection(vehicleId, inspection);
      // Mark as no work needed
      await api.markNoWorkNeeded(vehicleId);
      onBack();
    } catch (err) {
      alert('Failed to complete: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle "Create Repair Order" button
  const handleCreateRO = async () => {
    // Save inspection first
    await saveInspection();
    setShowCreateROModal(true);
  };

  // Loading state
  if (loading) {
    return <LoadingSpinner message="Loading inspection..." />;
  }

  // Error state
  if (error || !vehicle) {
    return <ErrorDisplay message={error || 'Vehicle not found'} />;
  }

  const isFullInspection = vehicle.inspectionType !== 'wholesale_platform';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-neutral-100 rounded-lg text-xl transition"
          title="Back to Dashboard"
        >
          ←
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold">Vehicle Inspection</h1>
          <p className="text-neutral-500">
            {vehicle.stockNumber} - {vehicle.year} {vehicle.make} {vehicle.model}
          </p>
        </div>
        {saving && (
          <span className="text-sm text-blue-600 flex items-center gap-1">
            <span className="animate-pulse">●</span> Saving...
          </span>
        )}
        {saved && !saving && (
          <span className="text-sm text-green-600 flex items-center gap-1">
            ✓ Saved
          </span>
        )}
      </div>

      {/* Dealer Direct Alert - RED background with checklist */}
      {vehicle.endUse === 'dealer_direct' && (
        <div className="p-4 bg-red-100 border-2 border-red-400 rounded-xl">
          <div className="flex items-center gap-2 text-red-800 font-bold text-lg mb-3">
            <span className="text-2xl">⚠️</span>
            DEALER DIRECT - THOROUGH INSPECTION REQUIRED
          </div>
          <div className="text-sm text-red-700 space-y-1 ml-8">
            <div>☐ 20 minute test drive</div>
            <div>☐ Hard acceleration test</div>
            <div>☐ Lift inspection - check suspension</div>
            <div>☐ Check for leaks</div>
            <div>☐ Record video of underbody</div>
          </div>
        </div>
      )}

      {/* Auction Lights */}
      {(vehicle.auctionLight || vehicle.auctionAnnouncements) && (
        <div className={`p-4 rounded-xl border-2 ${
          (vehicle.auctionLight || '').includes('red') 
            ? 'bg-red-50 border-red-300' 
            : (vehicle.auctionLight || '').includes('yellow')
              ? 'bg-yellow-50 border-yellow-300'
              : 'bg-green-50 border-green-300'
        }`}>
          <div className="text-xs text-neutral-500 font-medium mb-2">AUCTION SAID</div>
          <div className="flex items-center gap-3">
            <span className="text-2xl">
              {(vehicle.auctionLight || '').includes('green') && '🟢'}
              {(vehicle.auctionLight || '').includes('yellow') && '🟡'}
              {(vehicle.auctionLight || '').includes('red') && '🔴'}
            </span>
            <div>
              <div className="font-bold">
                {(vehicle.auctionLight || '').split(',').map(l => 
                  l === 'green' ? 'GREEN' : l === 'yellow' ? 'YELLOW' : l === 'red' ? 'RED' : ''
                ).filter(Boolean).join(' + ') || 'Not Set'}
              </div>
              {vehicle.auctionAnnouncements && (
                <div className="text-sm text-neutral-600 mt-1">
                  <span className="font-medium">Announcements:</span> {vehicle.auctionAnnouncements}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Inspection Type Banner (non-dealer-direct) */}
      {vehicle.endUse !== 'dealer_direct' && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          isFullInspection 
            ? 'bg-blue-100 border border-blue-300' 
            : 'bg-yellow-100 border border-yellow-300'
        }`}>
          <span className="text-2xl">{isFullInspection ? '' : ''}</span>
          <div>
            <div className="font-medium">
              {isFullInspection ? 'Full Inspection Required' : 'Basic Inspection (Wholesale Platform)'}
            </div>
            <div className="text-sm opacity-75">
              {isFullInspection 
                ? 'Complete all inspection sections thoroughly'
                : 'Quick check only - vehicle already inspected by platform'}
            </div>
          </div>
        </div>
      )}

      {/* Vehicle Information */}
      <div className="bg-white border border-neutral-200 rounded-xl p-4">
        <h2 className="font-semibold mb-3 text-lg">Vehicle Information</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-neutral-500 block text-xs mb-1">Stock #</span>
            <span className="font-mono font-medium">{vehicle.stockNumber}</span>
          </div>
          <div>
            <span className="text-neutral-500 block text-xs mb-1">Year</span>
            <span>{vehicle.year}</span>
          </div>
          <div>
            <span className="text-neutral-500 block text-xs mb-1">Make</span>
            <span>{vehicle.make}</span>
          </div>
          <div>
            <span className="text-neutral-500 block text-xs mb-1">Model</span>
            <span>{vehicle.model}</span>
          </div>
          <div className="md:col-span-2">
            <span className="text-neutral-500 block text-xs mb-1">VIN</span>
            <VinDisplay vin={vehicle.vin} />
          </div>
          <div>
            <span className="text-neutral-500 block text-xs mb-1">Mileage</span>
            <span>{vehicle.mileage?.toLocaleString() || '-'}</span>
          </div>
          <div>
            <span className="text-neutral-500 block text-xs mb-1">Color</span>
            <span>{vehicle.color || '-'}</span>
          </div>
        </div>

        {/* Pre-Check Notes (from vehicle entry) */}
        {(vehicle.whatItNeeds || vehicle.precheckNotes) && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-300 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl"></span>
              <span className="font-semibold text-amber-800">Pre-Check Notes (from vehicle entry)</span>
            </div>
            {vehicle.whatItNeeds && (
              <div className="mb-2">
                <div className="text-xs text-amber-700 font-medium mb-1">What It Needs:</div>
                <div className="text-sm bg-white p-2 rounded border border-amber-200">{vehicle.whatItNeeds}</div>
              </div>
            )}
            {vehicle.precheckNotes && (
              <div className="mb-2">
                <div className="text-xs text-amber-700 font-medium mb-1">Pre-Check Notes:</div>
                <div className="text-sm bg-white p-2 rounded border border-amber-200">{vehicle.precheckNotes}</div>
              </div>
            )}
            <div className="text-xs text-amber-600 mt-2 italic">
              ℹ️ This is a reminder of what was noted at vehicle entry. Please do a full arrival inspection.
            </div>
          </div>
        )}

        {/* Pre-Check Photos */}
        {(vehicle.whatItNeedsPhotos?.length > 0 || vehicle.precheckPhotos?.length > 0) && (
          <div className="mt-4">
            <div className="text-xs text-neutral-500 mb-2 font-medium">📷 Pre-Check Photos:</div>
            <PhotoGallery 
              photos={[...(vehicle.whatItNeedsPhotos || []), ...(vehicle.precheckPhotos || [])]} 
              editable={false} 
            />
          </div>
        )}

        {/* Parts Already Ordered */}
        {vehicle.partsPreordered?.length > 0 && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-xs text-blue-700 font-medium mb-2"> Parts Pre-Ordered:</div>
            <ul className="text-sm space-y-1">
              {vehicle.partsPreordered.map((part, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="text-blue-500">•</span>
                  {part.description || part}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Uploads Section */}
      <div className="bg-white border border-neutral-200 rounded-xl p-4">
        <h2 className="font-semibold mb-4 text-lg">Inspection Data & Uploads</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Walkaround Video */}
          <FileUpload
            accept="video/*"
            onUpload={handleVideoUpload}
            className="border-2 border-dashed border-neutral-200 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition"
          >
            <div className="text-4xl mb-2">🎥</div>
            <div className="font-medium text-sm mb-1">Inspection Video</div>
            {inspection.walkaroundVideoUrl ? (
              <a
                href={inspection.walkaroundVideoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline"
                onClick={e => e.stopPropagation()}
              >
                ✓ Video uploaded - Click to view
              </a>
            ) : (
              <div className="text-xs text-neutral-400">Drag & drop or click</div>
            )}
          </FileUpload>

          {/* Underbody Video */}
          <FileUpload
            accept="video/*"
            onUpload={handleUnderbodyVideoUpload}
            className="border-2 border-dashed border-neutral-200 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition"
          >
            <div className="text-4xl mb-2">🔧</div>
            <div className="font-medium text-sm mb-1">Underbody Video</div>
            {inspection.underbodyVideoUrl ? (
              <a
                href={inspection.underbodyVideoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline"
                onClick={e => e.stopPropagation()}
              >
                ✓ Video uploaded - Click to view
              </a>
            ) : (
              <div className="text-xs text-neutral-400">Drag & drop or click</div>
            )}
          </FileUpload>

          {/* BlueDriver/OBD Scan */}
          <FileUpload
            accept="image/*,.pdf"
            onUpload={handleScanUpload}
            className="border-2 border-dashed border-neutral-200 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition"
          >
            <div className="text-4xl mb-2">📱</div>
            <div className="font-medium text-sm mb-1">BlueDriver Scan</div>
            {inspection.bluedriverScanUrl ? (
              <a
                href={inspection.bluedriverScanUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline"
                onClick={e => e.stopPropagation()}
              >
                ✓ Scan uploaded - Click to view
              </a>
            ) : (
              <div className="text-xs text-neutral-400">Drag & drop or click</div>
            )}
          </FileUpload>

          {/* Photos */}
          <FileUpload
            accept="image/*"
            multiple
            onUpload={handlePhotoUpload}
            className="border-2 border-dashed border-neutral-200 rounded-lg p-6 text-center hover:border-blue-400 hover:bg-blue-50 transition"
          >
            <div className="text-4xl mb-2">📷</div>
            <div className="font-medium text-sm mb-1">
              Photos ({inspection.photos?.length || 0})
            </div>
            <div className="text-xs text-neutral-400">Drag & drop or click</div>
          </FileUpload>
        </div>

        {/* Photo Thumbnails */}
        {inspection.photos?.length > 0 && (
          <div className="mt-4">
            <div className="text-xs text-neutral-500 mb-2">Uploaded Photos:</div>
            <PhotoGallery
              photos={inspection.photos}
              onDelete={handlePhotoDelete}
              editable={true}
            />
          </div>
        )}
      </div>

      {/* Tech Assessment Section */}
      <div className="bg-white border border-neutral-200 rounded-xl p-4">
        <h2 className="font-semibold mb-4 text-lg">Tech Assessment</h2>
        
        {/* Structure Damage Checkbox - Always visible */}
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={inspection.structureDamageFound || false}
              onChange={e => {
                handleFieldChange('structureDamageFound', e.target.checked);
                saveInspection({ ...inspection, structureDamageFound: e.target.checked });
              }}
              className="w-5 h-5 rounded border-red-300 text-red-600 focus:ring-red-500"
            />
            <div>
              <span className="font-medium text-red-800">⚠️ Structure Damage Found</span>
              <p className="text-xs text-red-600">Check this if any frame/structure damage is discovered during inspection</p>
            </div>
          </label>
        </div>

        {/* Inspection Light Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-neutral-700 mb-2">Inspection Result</label>
          <div className="flex gap-3">
            {[
              { value: 'good', label: 'Good', color: 'green', icon: '🟢' },
              { value: 'issues', label: 'Issues', color: 'yellow', icon: '🟡' },
              { value: 'major', label: 'Major', color: 'red', icon: '🔴' }
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  handleFieldChange('inspectionLight', opt.value);
                  saveInspection({ ...inspection, inspectionLight: opt.value });
                }}
                className={`flex-1 py-3 px-4 rounded-lg border-2 transition flex items-center justify-center gap-2 ${
                  inspection.inspectionLight === opt.value
                    ? `border-${opt.color}-500 bg-${opt.color}-50`
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <span>{opt.icon}</span>
                <span className="font-medium">{opt.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* General Findings - Single text area */}
      <div className="bg-white border border-neutral-200 rounded-xl p-4">
        <h2 className="font-semibold mb-4 text-lg">General Findings / Opinions</h2>
        <textarea
          value={inspection.generalFindings || ''}
          onChange={e => handleFieldChange('generalFindings', e.target.value)}
          onBlur={handleFieldBlur}
          className="w-full h-48 px-4 py-3 border border-neutral-200 rounded-lg resize-none text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Enter your inspection findings, opinions, and recommendations here...

• What work does this vehicle need?
• Any issues found during inspection?
• Priority items or concerns?
• Overall assessment and recommendations"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pb-6">
        <button
          onClick={onBack}
          className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg transition"
        >
          Cancel
        </button>
        <button
          onClick={handleNoWorkNeeded}
          disabled={saving}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
        >
          ✓ No Work Needed
        </button>
        <button
          onClick={handleCreateRO}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
        >
          Create Repair Order →
        </button>
      </div>

      {/* Create RO Modal */}
      {showCreateROModal && (
        <CreateROModal
          vehicleId={vehicleId}
          vehicle={vehicle}
          users={users}
          onClose={() => setShowCreateROModal(false)}
          onCreated={onROCreated}
        />
      )}
    </div>
  );
};

// Create RO Modal Component
const CreateROModal = ({ vehicleId, vehicle, users, onClose, onCreated }) => {
  const [form, setForm] = useState({
    assignedToUserId: '',
    priority: 'normal',
    complaint: vehicle?.whatItNeeds || '',
    estimateOnly: true,
    gamePlanNote: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Dealer Direct checklist state
  const isDealerDirect = vehicle?.endUse === 'dealer_direct';
  const [checklist, setChecklist] = useState({
    testDrive: false,
    hardAccel: false,
    liftInspection: false,
    checkLeaks: false,
    underbodyVideo: false
  });
  
  const allChecklistComplete = !isDealerDirect || Object.values(checklist).every(v => v);

  const shopUsers = users.filter(u => u.role === 'shop' || u.role === 'admin');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Extract precheck parts if available
      let precheckParts = [];
      console.log('InspectionForm: Vehicle data:', vehicle);
      console.log('InspectionForm: Vehicle precheckData:', vehicle?.precheckData);
      
      if (vehicle?.precheckData) {
        try {
          const precheckData = JSON.parse(vehicle.precheckData);
          precheckParts = precheckData.partsNeeded || [];
          console.log('InspectionForm: Parsed precheck data:', precheckData);
          console.log('InspectionForm: Found precheck parts:', precheckParts);
        } catch (parseErr) {
          console.error('InspectionForm: Failed to parse precheck data:', parseErr);
          console.log('InspectionForm: Raw precheckData that failed:', vehicle.precheckData);
        }
      } else {
        console.log('InspectionForm: No precheck data found on vehicle');
      }
      
      const roData = {
        assignedToUserId: form.assignedToUserId,
        priority: form.priority,
        complaint: form.complaint,
        precheckParts: precheckParts, // Include precheck parts
        estimateOnly: form.estimateOnly,
        gamePlanNote: form.gamePlanNote
      };
      
      console.log('InspectionForm: Creating RO with data:', roData);
      
      const result = await api.createROFromInspection(vehicleId, roData);
      console.log('InspectionForm: RO creation result:', result);
      
      if (precheckParts.length > 0) {
        alert(` RO created from inspection with ${precheckParts.length} precheck parts!`);
      }
      
      // Pass the RO ID so we can navigate directly to it
      onCreated(result.roId || result.id);
    } catch (err) {
      alert('Failed to create RO: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b">
          <h2 className="font-semibold text-lg">Create Repair Order</h2>
          <p className="text-sm text-neutral-500">
            {vehicle?.stockNumber} - {vehicle?.year} {vehicle?.make} {vehicle?.model}
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Dealer Direct Checklist - must complete before creating RO */}
          {isDealerDirect && (
            <div className="p-3 bg-red-50 border-2 border-red-300 rounded-lg">
              <div className="flex items-center gap-2 text-red-800 font-bold text-sm mb-2">
                <span>⚠️</span>
                DEALER DIRECT - Confirm Inspection Complete
              </div>
              <div className="space-y-2 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checklist.testDrive}
                    onChange={e => setChecklist(c => ({ ...c, testDrive: e.target.checked }))}
                    className="w-4 h-4 rounded border-red-300 text-red-600 focus:ring-red-500"
                  />
                  <span className={checklist.testDrive ? 'text-green-700' : 'text-red-700'}>20 minute test drive completed</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checklist.hardAccel}
                    onChange={e => setChecklist(c => ({ ...c, hardAccel: e.target.checked }))}
                    className="w-4 h-4 rounded border-red-300 text-red-600 focus:ring-red-500"
                  />
                  <span className={checklist.hardAccel ? 'text-green-700' : 'text-red-700'}>Hard acceleration test completed</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checklist.liftInspection}
                    onChange={e => setChecklist(c => ({ ...c, liftInspection: e.target.checked }))}
                    className="w-4 h-4 rounded border-red-300 text-red-600 focus:ring-red-500"
                  />
                  <span className={checklist.liftInspection ? 'text-green-700' : 'text-red-700'}>Lift inspection - checked suspension</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checklist.checkLeaks}
                    onChange={e => setChecklist(c => ({ ...c, checkLeaks: e.target.checked }))}
                    className="w-4 h-4 rounded border-red-300 text-red-600 focus:ring-red-500"
                  />
                  <span className={checklist.checkLeaks ? 'text-green-700' : 'text-red-700'}>Checked for leaks</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checklist.underbodyVideo}
                    onChange={e => setChecklist(c => ({ ...c, underbodyVideo: e.target.checked }))}
                    className="w-4 h-4 rounded border-red-300 text-red-600 focus:ring-red-500"
                  />
                  <span className={checklist.underbodyVideo ? 'text-green-700' : 'text-red-700'}>Recorded video of underbody</span>
                </label>
              </div>
              {!allChecklistComplete && (
                <div className="mt-2 text-xs text-red-600 font-medium">
                  ⚠️ Complete all items above before creating RO
                </div>
              )}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium mb-1">Complaint / Work Needed</label>
            <textarea
              value={form.complaint}
              onChange={e => setForm(f => ({ ...f, complaint: e.target.value }))}
              className="w-full h-24 px-3 py-2 border border-neutral-200 rounded-lg resize-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe what needs to be done..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Game Plan Note</label>
            <textarea
              value={form.gamePlanNote}
              onChange={e => setForm(f => ({ ...f, gamePlanNote: e.target.value }))}
              className="w-full h-20 px-3 py-2 border border-neutral-200 rounded-lg resize-none focus:ring-2 focus:ring-blue-500"
              placeholder="Initial plan or strategy for this RO..."
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Assign To</label>
              <select
                value={form.assignedToUserId}
                onChange={e => setForm(f => ({ ...f, assignedToUserId: e.target.value }))}
                className="w-full h-10 px-3 border border-neutral-200 rounded-lg bg-white"
              >
                <option value="">Unassigned</option>
                {shopUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Priority</label>
              <select
                value={form.priority}
                onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                className="w-full h-10 px-3 border border-neutral-200 rounded-lg bg-white"
              >
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          
          <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="estimateOnlyInspection"
                checked={form.estimateOnly}
                onChange={e => setForm(f => ({ ...f, estimateOnly: e.target.checked }))}
                className="w-5 h-5 text-orange-600 border-orange-300 rounded focus:ring-orange-500 focus:ring-2 cursor-pointer mt-0.5"
              />
              <label htmlFor="estimateOnlyInspection" className="cursor-pointer flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-sm font-bold ${form.estimateOnly ? 'text-orange-700' : 'text-green-700'}`}>
                    {form.estimateOnly ? '☐ Estimate Only' : '☑ Proceeding with Repairs'}
                  </span>
                </div>
                <div className="text-xs text-neutral-600">
                  {form.estimateOnly ? (
                    <span>Parts list for review, no order placed. <strong className="text-orange-700">Does NOT affect vehicle price.</strong></span>
                  ) : (
                    <span>Parts ordered/approved. <strong className="text-green-700">DOES affect vehicle price.</strong></span>
                  )}
                </div>
              </label>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-600 hover:bg-neutral-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !allChecklistComplete}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create RO'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InspectionForm;
